import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Equipment } from '../../../equipment/models/equipment';
import { Element } from '../../../equipment/models/elements/element';

import * as THREE from 'three';
import { FontLoader } from 'three/addons/loaders/FontLoader.js';
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Figure } from '../../../equipment/models/figures/figure';
import { Rectangle } from '../../../equipment/models/figures/rectangle';
import { RectangleSliced } from '../../../equipment/models/figures/rectangle-sliced';
import { Trapeze } from '../../../equipment/models/figures/trapeze';
import { Cylinder } from '../../../equipment/models/figures/cylinder';
import { EquipmentBase } from '../../../planogram/models/equipment-base';
import { ProductGroup } from '../../../planogram/models/product-group';
import { MaterialCache } from './material-cache';
import { konvaSettings } from '../../../equipment/models/konvaSettings';
import { getElementFillColor, getElementOuterFillColor } from '../../../equipment/models/elementFillColor';
import { HighlightOptionsValue, HighlightProductsService } from '../../services/highlight-products.service';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { elementsGenerator } from '../../../equipment/models/elements/elementGenerator';
import { BarrierParams, BarrierPoint, BarrierSide, BarrierType, BrandingContour, BrandingImage, BrandingMeshFace, BrandingParams, BrandingWallSide, Extension, ExtensionType, findBrandingImage, getBarrierMeshBarPlacements, getBrandingFaces, getBrandingMeshFaces, getBoxInnerMeshHorizontalOffsets, getBoxInnerMeshVerticalOffsets, getFreeSidesForBarrier, getMeshBarCenters, getPointAlongPolyline, getPolylineLength, getRoundedBarrierPolylines, getSlicedSlopeBars, isBrandingSideEnabled, mapBrandingToFaceSize, BARRIER_THICKNESS, MESH_BAR_THICKNESS, RailsParams, RoundingParams, loadBrandingImage } from '../../../equipment/models/elements/extension';
import { Utils } from '../../services/utils.service';
import { SessionService } from '../../services/session.service';
import { EMPTY_PRODUCT_IMAGE_PATH } from '../../services/products.service';
import { POSMaterial } from '../../../planogram/models/POSMaterials/POSMaterial';
import { POSTextMaterial } from '../../../planogram/models/POSMaterials/POSTextMaterial';
import { POSTextHorizontalAlign, POSTextVerticalAlign } from '../../../planogram/models/POSMaterials/posTextAlign';
import { POSImageMaterial } from '../../../planogram/models/POSMaterials/POSImageMaterial';
import { getFrontCornerRadii, pathRoundedRect } from '../../../equipment/models/elements/elementDrawer';

import { SHARED_MODULES } from '../../../shared.';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';

const COLORS = [
        // {mainColor: 0x990000, borderColor: 0x660000},
        {mainColor: 0x992600, borderColor: 0x661a00},
        {mainColor: 0x994d00, borderColor: 0x663300},
        // {mainColor: 0x997300, borderColor: 0x664d00},
        // {mainColor: 0x999900, borderColor: 0x666600},
        // {mainColor: 0x739900, borderColor: 0x4d6600},
        {mainColor: 0x4d9900, borderColor: 0x336600},
        {mainColor: 0x269900, borderColor: 0x1a6600},
        // {mainColor: 0x009900, borderColor: 0x006600},
        {mainColor: 0x009926, borderColor: 0x00661a},
        {mainColor: 0x00994d, borderColor: 0x006633},
        {mainColor: 0x009973, borderColor: 0x00664d},
        {mainColor: 0x009999, borderColor: 0x006666},
        {mainColor: 0x007399, borderColor: 0x004d66},
        {mainColor: 0x004d99, borderColor: 0x003366},
        {mainColor: 0x002699, borderColor: 0x001a66},
        {mainColor: 0x000099, borderColor: 0x000066},
        {mainColor: 0x260099, borderColor: 0x1a0066},
        {mainColor: 0x4d0099, borderColor: 0x330066},
        {mainColor: 0x730099, borderColor: 0x4d0066},
        {mainColor: 0x990099, borderColor: 0x660066},
        {mainColor: 0x990073, borderColor: 0x66004d},
        {mainColor: 0x99004d, borderColor: 0x660033},
        {mainColor: 0x990026, borderColor: 0x66001a},
    ];

const POS_TEXT_LABEL_DEPTH_M = 0.01;
const POS_TEXT_CANVAS_FONT_DIVISOR = 400;
const POS_TEXT_3D_SIZE_DIVISOR = 480;
const POS_TEXT_WRAP_REF_SCALE = 50;
const POS_TEXT_KONVA_PADDING_PX = 4;
const ROBOTO_CONDENSED_FONT_URL = '/static/assets/json/Roboto_Condensed_Regular.json';
const MASTER_POSITION_DASH: Array<number> = [12, 10];
const INACTIVE_EQUIPMENT_OPACITY = 0.4;

interface BrandingGeometry {
	geometry: THREE.BufferGeometry;
	followsContour: boolean;
}

@Component({
    selector: 'app-equipment-model',
    templateUrl: './equipment-model.component.html',
    styleUrls: ['./equipment-model.component.scss'],
	imports: [SHARED_MODULES, PhButtonComponent, PhIconComponent],
    standalone: true
})
export class EquipmentModelComponent implements OnDestroy {
    @ViewChild('canvas', {static: true}) canvas: ElementRef;

    @Input() products: boolean = false;
    @Input() photos: boolean = true;
    @Input() fullHeightPreview: boolean = false;
    @Input() parent: HTMLElement;

    @Input('blocks') set setBlock(data) {
        this.blocks = data;

        if (this.inited) {
            void this.updateScene();
            this.render();
        }
    }

	@Input('POSMaterials') set setPOSMaterials(data: Array<POSMaterial>) {
		this.POSMaterials = data ?? [];

		if (this.inited) {
			void this.updateScene();
			this.render();
		}
	}

    rotatedProducts: any;
    highlightProducts: any;

    @Input('stand') set updateStand(value: Equipment | Equipment[]) {
        this.stand = Array.isArray(value) ? value : [value];

        if (!this.inited) {
            this.init();
        } else {
            void this.updateScene();
            this.render();
        }
    }

    stand: Equipment | Equipment[];
    blocks: any[];
    POSMaterials: Array<POSMaterial> = [];
    renderer: THREE.WebGLRenderer;
    camera: THREE.PerspectiveCamera;
    scene: THREE.Scene;
    animationFrame: number;
	updating: boolean = false;
	private pendingUpdate: boolean = false;

    cache: MaterialCache = new MaterialCache();
    controls: OrbitControls;
	private _asyncObjects: Array<THREE.Object3D> = [];

    @Input('rotatedProducts') set SetRotatedProducts(data) {
        this.rotatedProducts = data;
    }

    @Input('highlightValue') highlightValue: HighlightOptionsValue;

    @Input('highlightProducts') set SetHighlightProducts(data) {
        this.highlightProducts = data;
    }

    @Output('error') error: EventEmitter<any> = new EventEmitter<any>();

    inited: boolean = false;
    webglError: boolean = false;

    constructor(
        private highlightProductsService: HighlightProductsService,
        private sessionService: SessionService
    ) {}

    static checkImage(src) {
        return new Promise(resolve => {
            const image = new Image();
            image.src = src;

            image.onload = () => {
                resolve(true);
            };

            image.onerror = () => {
                resolve(false);
            }
        });
    }

	private createTextureWithBackground(
		src: string,
		useTransparentBackground: boolean,
		onLoad: (texture: THREE.Texture) => void
	): void {
		new THREE.TextureLoader().load(src, (texture) => {
			const imageElement = texture.image as HTMLImageElement;
			const image = imageElement as CanvasImageSource;
			const width = Math.max(imageElement.naturalWidth || imageElement.width || 1, 1);
			const height = Math.max(imageElement.naturalHeight || imageElement.height || 1, 1);
			const canvas = document.createElement('canvas');
			canvas.width = width;
			canvas.height = height;

			const context = canvas.getContext('2d');
			if (!context) {
				onLoad(texture);
				return;
			}

			if (useTransparentBackground) {
				context.clearRect(0, 0, width, height);
			} else {
				context.fillStyle = '#ffffff';
				context.fillRect(0, 0, width, height);
			}
			context.drawImage(image, 0, 0, width, height);
			const transparentTexture = new THREE.CanvasTexture(canvas);
			transparentTexture.colorSpace = THREE.SRGBColorSpace;
			transparentTexture.generateMipmaps = false;
			transparentTexture.minFilter = THREE.LinearFilter;
			transparentTexture.wrapS = THREE.ClampToEdgeWrapping;
			transparentTexture.wrapT = THREE.ClampToEdgeWrapping;
			transparentTexture.needsUpdate = true;
			onLoad(transparentTexture);
		});
	}

    getElementMaterial(element: Element, shape?: Figure) {
		const fillColor = getElementFillColor(element, shape);

        switch (element.parent.type) {
            case EquipmentElements.Hole:
                return new THREE.MeshPhongMaterial({
                    colorWrite: false,
                    specular: 0x0,
                    shininess: 0
                });
            case EquipmentElements.Showcase:
                return new THREE.MeshPhongMaterial({
                    color: 0xccddff,
                    transparent: true,
                    opacity: 0.75,
                    specular: 0xaaaaaa,
                    shininess: 100
                });
            default:
                return new THREE.MeshPhongMaterial({
                    color: fillColor,
                    specular: 0xaaaaaa,
                    shininess: 100
                });
        }
    }

    private addBarrierObject(object3D: THREE.Object3D, shape: Figure, element: Element, shadow: boolean) {
		const extension = element.getExtension<BarrierParams>(ExtensionType.Barrier);

		if (!extension?.active) {
			return;
		}

		const { barrierType, barrierHeight, barrierColor } = extension.params;

		if (barrierType === BarrierType.None) {
			return;
		}

		const equipment =
			element.stand ??
			(Array.isArray(this.stand)
				? this.stand.find(({ allElements }) => allElements?.some(({ id }) => id === element.id))
				: this.stand);

		if (!equipment) {
			return;
		}

		const transparent = barrierType === BarrierType.Transparent;
		const material = new THREE.MeshPhongMaterial({
			color: transparent ? 0xffffff : barrierColor,
			transparent,
			opacity: transparent ? 0.35 : 1,
			specular: 0xaaaaaa,
			shininess: 100
		});
		// Yes those two should also not be there. Hope I will figure out a way to do so without creating another abomination
		const addBox = (group: THREE.Group, width: number, height: number, x: number = 0, y: number = 0) => {
			const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, BARRIER_THICKNESS), material);

			mesh.castShadow = mesh.receiveShadow = shadow;
			mesh.position.set(x, y, 0);

			group.add(mesh);
		};
		const sides = getFreeSidesForBarrier(element, equipment as Equipment);
		const centerY = ((shape.height ?? 0) + barrierHeight) / 2;
		const addSegment = (start: BarrierPoint, end: BarrierPoint, extend: boolean) => {
			const dx = end.x - start.x;
			const dz = end.z - start.z;
			const edgeLength = Math.hypot(dx, dz);

			if (edgeLength < 1e-8) {
				return;
			}

			const dirX = dx / edgeLength;
			const dirZ = dz / edgeLength;
			const length = extend ? edgeLength + BARRIER_THICKNESS : edgeLength;
			const group = new THREE.Group();

			group.position.set(
				(start.x + end.x - (extend ? dirZ * BARRIER_THICKNESS : 0)) / 2,
				centerY,
				(start.z + end.z + (extend ? dirX * BARRIER_THICKNESS : 0)) / 2
			);
			group.rotation.y = Math.atan2(-dirZ, dirX);

			if (barrierType === BarrierType.Mesh) {
				const bar = BARRIER_THICKNESS;
				const railY = barrierHeight / 2 - bar / 2;

				addBox(group, length, bar, 0, railY);
				addBox(group, length, bar, 0, -railY);

				if (extend) {
					const half = length / 2 - bar / 2;

					getBarrierMeshBarPlacements(length).forEach(offset => {
						addBox(group, bar, barrierHeight, THREE.MathUtils.clamp(offset - length / 2, -half, half));
					});
				}
			} else {
				addBox(group, length, barrierHeight);
			}

			object3D.add(group);
		};

		if (shape.type === 'trapeze') {
			const frontWidth = shape.frontWidth ?? 0;
			const backWidth = shape.backWidth ?? 0;
			const halfDepth = shape.depth / 2;
			const frontLeft = { x: -frontWidth / 2, z: halfDepth };
			const frontRight = { x: frontWidth / 2, z: halfDepth };
			const backLeft = { x: -backWidth / 2, z: -halfDepth };
			const backRight = { x: backWidth / 2, z: -halfDepth };

			sides.forEach(side => {
				switch (side) {
					case BarrierSide.Front: {
						addSegment(frontLeft, frontRight, true);

						break;
					}

					case BarrierSide.Right: {
						addSegment(frontRight, backRight, true);

						break;
					}

					case BarrierSide.Back: {
						addSegment(backRight, backLeft, true);

						break;
					}

					case BarrierSide.Left: {
						addSegment(backLeft, frontLeft, true);

						break;
					}
				}
			});

			return;
		}

		const rounding = element.getExtension<RoundingParams>(ExtensionType.Rounding);
		const left = rounding?.active ? Utils.convertPercentToRadius(rounding.params.leftSideRadius, element) : 0;
		const right = rounding?.active ? Utils.convertPercentToRadius(rounding.params.rightSideRadius, element) : 0;

		getRoundedBarrierPolylines(shape.width ?? 0, shape.depth, left, right, sides).forEach(({ points, closed }) => {
			const count = closed ? points.length : points.length - 1;

			for (let index = 0; index < count; index++) {
				addSegment(points[index], points[(index + 1) % points.length], false);
			}

			if (barrierType !== BarrierType.Mesh) {
				return;
			}

			const length = getPolylineLength(points, closed);
			const placements = getBarrierMeshBarPlacements(length);
			const offsets = closed && placements.length > 1 ? placements.slice(0, -1) : placements;

			offsets.forEach(offset => {
				const point = getPointAlongPolyline(points, closed, offset);

				if (!point) {
					return;
				}

				const mesh = new THREE.Mesh(
					new THREE.BoxGeometry(BARRIER_THICKNESS, barrierHeight, BARRIER_THICKNESS),
					material
				);

				mesh.castShadow = mesh.receiveShadow = shadow;
				mesh.position.set(point.x, centerY, point.z);
				object3D.add(mesh);
			});
		});
	}

	private addPartitionMeshObject(
		object3D: THREE.Object3D,
		rect: Rectangle,
		material: THREE.Material,
		shadow: boolean
	) {
		const bar = Math.min(MESH_BAR_THICKNESS, rect.width, rect.height / 2, rect.depth / 2);

		if (bar <= 0) {
			return;
		}

		const addBox = (width: number, height: number, depth: number, x: number, y: number, z: number) => {
			const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);

			mesh.castShadow = mesh.receiveShadow = shadow;
			mesh.position.set(x, y, z);
			object3D.add(mesh);
		};
		const railY = rect.height / 2 - bar / 2;
		const halfDepth = rect.depth / 2;
		const halfBar = bar / 2;

		addBox(rect.width, bar, rect.depth, 0, railY, 0);
		addBox(rect.width, bar, rect.depth, 0, -railY, 0);

		getMeshBarCenters(rect.depth, bar).forEach((center) => {
			addBox(rect.width, rect.height, bar, 0, 0, center - halfDepth);
		});
	}

	private addSideWallOuterMesh(object3D: THREE.Object3D, element: Element, rect: Rectangle, shadow: boolean): void {
		const outerMaterial = new THREE.MeshPhongMaterial({
			color: getElementOuterFillColor(element, rect),
			specular: 0xaaaaaa,
			shininess: 100
		});

		if (!shadow) {
			outerMaterial.setValues({
				opacity: INACTIVE_EQUIPMENT_OPACITY,
				transparent: true
			});
		}

		const isLeftWall = element.shapes[0] === rect;
		const overlayThickness = 0.001;
		const outerMesh = new THREE.Mesh(
			new THREE.BoxGeometry(overlayThickness, rect.height, rect.depth),
			outerMaterial
		);

		outerMesh.castShadow = shadow;
		outerMesh.receiveShadow = shadow;
		outerMesh.position.set(
			(isLeftWall ? -1 : 1) * (rect.width / 2 + overlayThickness / 2),
			0,
			0
		);
		object3D.add(outerMesh);
	}

	private addBrandingObject(object3D: THREE.Object3D, shape: Figure, element: Element) {
		const branding = element.getExtension<BrandingParams>(ExtensionType.Branding);

		if (!branding?.active) {
			return;
		}

		if (element.parent.is(EquipmentElements.SideWalls)) {
			this.addSideWallBranding(object3D, shape as Rectangle, element, branding);
			return;
		}

		const shapeIndex = element.shapes.indexOf(shape);
		const placed = findBrandingImage(branding.params.images, shapeIndex);

		if (!placed || shapeIndex < 0) {
			return;
		}

		const faceWidth = shape.frontWidth ?? shape.width ?? 0;
		const faceHeight = shape.height ?? 0;
		const faceDepth = shape.depth ?? 0;
		const offset = 0.001;
		const equipment =
			element.stand ??
			(Array.isArray(this.stand)
				? this.stand.find(({ allElements }) => allElements?.some(({ id }) => id === element.id))
				: this.stand);
		const meshFaces = getBrandingMeshFaces(element, equipment ?? null);
		const contour = this.getBrandingContour(element, shape);

		meshFaces.forEach((meshFace: BrandingMeshFace) => {
			const isBack = meshFace === 'back';

			this.drawBrandingPlane(
				object3D,
				placed,
				branding,
				faceWidth,
				faceHeight,
				isBack ? Math.PI : 0,
				0,
				isBack ? -faceDepth / 2 - offset : faceDepth / 2 + offset,
				isBack ? null : contour,
				element
			);
		});
	}

	private addSideWallBranding(
		object3D: THREE.Object3D,
		shape: Rectangle,
		element: Element,
		branding: Extension<BrandingParams>
	) {
		const faces = getBrandingFaces(element);
		const isLeftWall = element.shapes[0] === shape;
		const faceWidth = shape.depth ?? 0;
		const faceHeight = shape.height ?? 0;
		const wallOffset = (shape.width ?? 0) / 2 + 0.002;

		faces.forEach((face) => {
			if (!isBrandingSideEnabled(branding.params, face.side)) {
				return;
			}

			const placed = findBrandingImage(branding.params.images, face.shapeIndex);

			if (!placed) {
				return;
			}

			const mapped = mapBrandingToFaceSize(placed, face, faceWidth, faceHeight);
			const inner = face.side === BrandingWallSide.Inner;
			const onPlusX = inner === isLeftWall;

			this.drawBrandingPlane(
				object3D,
				mapped,
				branding,
				faceWidth,
				faceHeight,
				onPlusX ? Math.PI / 2 : -Math.PI / 2,
				onPlusX ? wallOffset : -wallOffset,
				0,
				null,
				element
			);
		});
	}

	private getBrandingContour(element: Element, shape: Figure): BrandingContour | null {
		if (shape.type !== 'rectangle') {
			return null;
		}

		const rounding = element.getExtension<RoundingParams>(ExtensionType.Rounding);
		const leftRadius = rounding?.active
			? Utils.convertPercentToRadius(rounding.params.leftSideRadius, element)
			: 0;
		const rightRadius = rounding?.active
			? Utils.convertPercentToRadius(rounding.params.rightSideRadius, element)
			: 0;

		if (leftRadius <= 0 && rightRadius <= 0) {
			return null;
		}

		return {
			width: shape.width ?? 0,
			depth: shape.depth ?? 0,
			leftRadius,
			rightRadius
		};
	}

	private createFlatBrandingGeometry(faceWidth: number, faceHeight: number): BrandingGeometry {
		return { geometry: new THREE.PlaneGeometry(faceWidth, faceHeight), followsContour: false };
	}

	private appendContourStrip(
		points: Array<BarrierPoint>,
		contour: BrandingContour,
		faceHeight: number,
		positions: Array<number>,
		uvs: Array<number>,
		indices: Array<number>,
		vertex: number
	): number {
		points.forEach((point) => {
			const u = THREE.MathUtils.clamp((point.x + contour.width / 2) / (contour.width || 1), 0, 1);

			positions.push(point.x, -faceHeight / 2, point.z);
			uvs.push(u, 0);
			positions.push(point.x, faceHeight / 2, point.z);
			uvs.push(u, 1);
		});

		for (let index = 1; index < points.length; index++) {
			const bottomPrev = vertex + (index - 1) * 2;
			const topPrev = bottomPrev + 1;
			const bottom = bottomPrev + 2;
			const top = bottomPrev + 3;

			indices.push(bottomPrev, bottom, topPrev, topPrev, bottom, top);
		}

		return vertex + points.length * 2;
	}

	private createBrandingGeometry(
		faceWidth: number,
		faceHeight: number,
		contour: BrandingContour | null
	): BrandingGeometry {
		if (!contour) {
			return this.createFlatBrandingGeometry(faceWidth, faceHeight);
		}

		const polylines = getRoundedBarrierPolylines(
			contour.width,
			contour.depth,
			contour.leftRadius,
			contour.rightRadius,
			[BarrierSide.Front],
			0.001
		);

		if (!polylines.some((polyline) => polyline.points.length > 1)) {
			return this.createFlatBrandingGeometry(faceWidth, faceHeight);
		}

		const positions: Array<number> = [];
		const uvs: Array<number> = [];
		const indices: Array<number> = [];
		let vertex = 0;

		polylines.forEach(({ points }) => {
			if (points.length < 2) {
				return;
			}

			vertex = this.appendContourStrip(points, contour, faceHeight, positions, uvs, indices, vertex);
		});

		const geometry = new THREE.BufferGeometry();

		geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
		geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
		geometry.setIndex(indices);
		geometry.computeVertexNormals();

		return { geometry, followsContour: true };
	}

	private resolveBrandingOpacity(element: Element | undefined, branding: Extension<BrandingParams>): number {
		const opacity = branding.params.opacity;

		if (
			element?.hasExtension(ExtensionType.Availability)
			&& !element.isExtensionActive(ExtensionType.Availability)
		) {
			return opacity * INACTIVE_EQUIPMENT_OPACITY;
		}

		return opacity;
	}

	private drawBrandingPlane(
		object3D: THREE.Object3D,
		placed: BrandingImage,
		branding: Extension<BrandingParams>,
		faceWidth: number,
		faceHeight: number,
		rotationY: number,
		x: number,
		z: number,
		contour: BrandingContour | null = null,
		element?: Element
	) {
		loadBrandingImage(placed.image)
			.then((source) => {
				if (!faceWidth || !faceHeight) {
					return;
				}

				const px = 1024 / Math.max(faceWidth, faceHeight);
				const canvas = document.createElement('canvas');

				canvas.width = Math.max(1, Math.round(faceWidth * px));
				canvas.height = Math.max(1, Math.round(faceHeight * px));

				const context = canvas.getContext('2d');

				if (!context) {
					return;
				}

				const radii = element ? getFrontCornerRadii(element, canvas.width, canvas.height) : null;

				if (radii) {
					pathRoundedRect(context, canvas.width, canvas.height, radii);
				} else {
					context.beginPath();
					context.rect(0, 0, canvas.width, canvas.height);
				}

				context.clip();
				const brandingWidthPx = placed.width * px;
				const brandingHeightPx = placed.height * px;
				const bottomLeftX = placed.x * px;
				const bottomLeftY = canvas.height - placed.y * px;

				context.translate(bottomLeftX + brandingWidthPx / 2, bottomLeftY - brandingHeightPx / 2);
				context.rotate((branding.params.rotation * Math.PI) / 180);
				context.drawImage(
					source,
					-brandingWidthPx / 2,
					-brandingHeightPx / 2,
					brandingWidthPx,
					brandingHeightPx
				);

				const texture = new THREE.CanvasTexture(canvas);

				texture.colorSpace = THREE.SRGBColorSpace;
				texture.needsUpdate = true;
				texture.generateMipmaps = false;
				texture.minFilter = THREE.LinearFilter;
				texture.wrapS = THREE.ClampToEdgeWrapping;
				texture.wrapT = THREE.ClampToEdgeWrapping;

				const { geometry, followsContour } = this.createBrandingGeometry(faceWidth, faceHeight, contour);
				const material = new THREE.MeshBasicMaterial({
					map: texture,
					transparent: true,
					opacity: this.resolveBrandingOpacity(element, branding),
					side: THREE.FrontSide,
					depthWrite: false
				});
				const mesh = new THREE.Mesh(geometry, material);

				mesh.rotation.y = followsContour ? 0 : rotationY;
				mesh.position.set(followsContour ? 0 : x, 0, followsContour ? 0 : z);
				object3D.add(mesh);
			})
			.catch(() => undefined);
	}

    getShapeObject(shape: Figure, element: Element, shadow = true): THREE.Object3D {
		const material: THREE.MeshPhongMaterial = this.getElementMaterial(element, shape);

		if (element.hasExtension(ExtensionType.Availability) && !element.isExtensionActive(ExtensionType.Availability)) {
			shadow = false;
			material.setValues({
				opacity: INACTIVE_EQUIPMENT_OPACITY,
				transparent: true
			});
		}

        switch (shape.constructor.name) {
            case 'Trapeze': {
                const trapeze: Trapeze = shape as Trapeze;
				const object3D = new THREE.Object3D();

				const path = new THREE.Shape();
				path.moveTo(-trapeze.backWidth / 2, -trapeze.depth / 2);
				path.lineTo(trapeze.backWidth / 2, -trapeze.depth / 2);
				path.lineTo(trapeze.frontWidth / 2, trapeze.depth / 2);
				path.lineTo(-trapeze.frontWidth / 2, trapeze.depth / 2);
				path.closePath();

				const extrudeSettings = { bevelEnabled: false, curveSegments: 64, depth: trapeze.height };
				const geometry = new THREE.ExtrudeGeometry(path, extrudeSettings);
				geometry.rotateX(Math.PI / 2);

                const mesh = new THREE.Mesh(geometry, material);
                mesh.castShadow = shadow;
                mesh.receiveShadow = shadow;
				mesh.position.y = trapeze.height / 2;

                object3D.add(mesh);

				this.addBarrierObject(object3D, trapeze, element, shadow);
				this.addBrandingObject(object3D, trapeze, element);

                object3D.position.x = (trapeze.begin.x + Math.max(trapeze.frontWidth, trapeze.backWidth) / 2);
                object3D.position.y = (trapeze.begin.y + trapeze.height / 2);
                object3D.position.z = (trapeze.begin.z + trapeze.depth / 2);

                return object3D;
            }
            case 'Rectangle': {
				const rect: Rectangle = shape as Rectangle;
				const object3D = new THREE.Object3D();
				const isMeshPartition =
					element.parent.is(EquipmentElements.Partition) && element.isExtensionActive(ExtensionType.Mesh);
				let mesh: THREE.Mesh | undefined;

				if (isMeshPartition) {
					this.addPartitionMeshObject(object3D, rect, material, shadow);
				} else {
				const path = new THREE.Shape();

				const rounding: Extension<RoundingParams> = element.getExtension(ExtensionType.Rounding);
				const leftSideRadius: number = rounding?.active ? Utils.convertPercentToRadius(rounding.params.leftSideRadius, element) : 0;
				const rightSideRadius: number = rounding?.active ? Utils.convertPercentToRadius(rounding.params.rightSideRadius, element) : 0;
				const halfWidth: number = rect.width / 2;
				const halfHeight: number = rect.depth / 2;

				if (leftSideRadius === 0 && rightSideRadius === 0) {
					path.moveTo(-halfWidth, -halfHeight);
					path.lineTo(halfWidth, -halfHeight);
					path.lineTo(halfWidth, halfHeight);
					path.lineTo(-halfWidth, halfHeight);
					path.lineTo(-halfWidth, -halfHeight);
				} else {
					path.moveTo(-halfWidth + leftSideRadius, -halfHeight);
					path.lineTo(halfWidth - rightSideRadius, -halfHeight);
					if (rightSideRadius > 0) {
						path.absarc(halfWidth - rightSideRadius, -halfHeight + rightSideRadius, rightSideRadius, -Math.PI / 2, 0, false);
					}
					path.lineTo(halfWidth, halfHeight - rightSideRadius);
					if (rightSideRadius > 0) {
						path.absarc(halfWidth - rightSideRadius, halfHeight - rightSideRadius, rightSideRadius, 0, Math.PI / 2, false);
					}
					path.lineTo(-halfWidth + leftSideRadius, halfHeight);
					if (leftSideRadius > 0) {
						path.absarc(-halfWidth + leftSideRadius, halfHeight - leftSideRadius, leftSideRadius, Math.PI / 2, Math.PI, false);
					}
					path.lineTo(-halfWidth, -halfHeight + leftSideRadius);
					if (leftSideRadius > 0) {
						path.absarc(-halfWidth + leftSideRadius, -halfHeight + leftSideRadius, leftSideRadius, Math.PI, (3 * Math.PI) / 2, false);
					}
					path.lineTo(-halfWidth + leftSideRadius, -halfHeight);
				}

				const extrudeSettings = { bevelEnabled: false, curveSegments: 64, depth: rect.height };
				const geometry = new THREE.ExtrudeGeometry(path, extrudeSettings);
				geometry.rotateX(Math.PI / 2);

				mesh = new THREE.Mesh(geometry, material);
                mesh.castShadow = shadow;
                mesh.receiveShadow = shadow;
				mesh.position.y = rect.height / 2;

                object3D.add(mesh);

				if (element.parent.is(EquipmentElements.SideWalls)) {
					this.addSideWallOuterMesh(object3D, element, rect, shadow);
				}

                if (['Hook'].includes(element.parent.type)) {
                    const hook = new THREE.Object3D();
					const frontGeometry = new THREE.BoxGeometry(rect.width + 0.001, rect.height, rect.height);
					const frontMesh = new THREE.Mesh(frontGeometry, material);

                    frontMesh.castShadow = shadow;
                    frontMesh.receiveShadow = shadow;

                    hook.add(frontMesh);

					const diameter: number = Math.sqrt(Math.pow(rect.width, 2) + Math.pow(rect.height, 2));
                    hook.rotation.x = -Math.PI / 4;
					hook.position.y = diameter / 2 - rect.height / 2;
					hook.position.z = rect.depth / 2;

                    object3D.add(hook);
                }
				}

				this.addBarrierObject(object3D, rect, element, shadow);
				this.addBrandingObject(object3D, rect, element);

                if (rect.angle == 0) {
                    object3D.position.x = rect.begin.x + rect.width / 2;
                }
                if (rect.angle <= 180 && rect.angle != 0) {
                    object3D.position.x = rect.begin.x + rect.width - (rect.getBoundingX().max - rect.getBoundingX().min) / 2;
                    object3D.rotation.y = -rect.angle * Math.PI / 180;
                }
                if (rect.angle > 180) {
                    object3D.position.x = rect.begin.x + (rect.getBoundingX().max - rect.getBoundingX().min) / 2;
                    object3D.rotation.y = -rect.angle * Math.PI / 180;
                }

                if (rect.frontAngle == 0) {
                    object3D.position.y = rect.begin.y + rect.height / 2;
                    object3D.position.z = rect.begin.z + (rect.getBoundingZ().max - rect.getBoundingZ().min) / 2;
                } else {
                    object3D.position.setY(rect.begin.y + rect.height / 2 - rect.rotationPoint.y);
                    object3D.position.setZ(rect.begin.z + rect.depth / 2 - rect.rotationPoint.x);
                    const obj1 = new THREE.Object3D();
                    obj1.add(object3D);
                    obj1.rotation.x = rect.frontAngle * Math.PI / 180;
                    obj1.position.y += rect.rotationPoint.y;
                    obj1.position.z += rect.rotationPoint.x;
                    return obj1;
                }

                if (mesh && ['Hole'].includes(element.parent.type)) {
                    const dependBoundingZ = element.depend.shapes[0].getBoundingZ();
                    object3D.position.z = dependBoundingZ.max - (element.Depth / 2) + .001;
                    mesh.renderOrder = 0;
                }

                if (mesh && ['Perforation', 'PerforationWall'].includes(element.parent.type)) {
                    mesh.renderOrder = 1
                }

                return object3D;
            }
            case 'RectangleSliced': {
				const rect: RectangleSliced = shape as RectangleSliced;
				const object3D = new THREE.Object3D();
				const isMeshBox =
					element.parent.is(EquipmentElements.SlicedBox) && element.isExtensionActive(ExtensionType.Mesh);

				if (isMeshBox) {
					this.addSlicedBoxMeshWalls(object3D, rect, material, shadow);
				} else {
				const path = new THREE.Shape();
				path.moveTo(-rect.depth / 2, 0);
				path.lineTo(-rect.depth / 2, rect.backHeight);
				path.lineTo(rect.depth / 2, rect.frontHeight);
				path.lineTo(rect.depth / 2, 0);
				path.closePath();

				const extrudeSettings = { bevelEnabled: false, curveSegments: 64, depth: rect.wallWidth };
				const sideGeometry = new THREE.ExtrudeGeometry(path, extrudeSettings);
				sideGeometry.rotateY(-Math.PI / 2);

                const frontGeometry = new THREE.BoxGeometry(rect.width, rect.frontHeight, rect.wallWidth, 1, 1, 1);
				const backGeometry = new THREE.BoxGeometry(rect.width, rect.backHeight, rect.wallWidth, 1, 1, 1);

				const leftMesh = new THREE.Mesh(sideGeometry, material);
                leftMesh.position.x = - rect.width / 2;
                leftMesh.position.y = rect.begin.y;
				leftMesh.castShadow = shadow;
                leftMesh.receiveShadow = shadow;

                object3D.add(leftMesh);

                const rightMesh = new THREE.Mesh(sideGeometry, material);
                rightMesh.position.x = rect.width / 2;
                rightMesh.position.y = rect.begin.y;
                rightMesh.castShadow = shadow;
                rightMesh.receiveShadow = shadow;

                object3D.add(rightMesh);

                const backMesh = new THREE.Mesh(backGeometry, material);
                backMesh.position.z = rect.wallWidth / 2 - rect.depth / 2;
                backMesh.position.y = rect.begin.y + rect.backHeight / 2;
                backMesh.castShadow = shadow;
                backMesh.receiveShadow = shadow;

                object3D.add(backMesh);

                const frontMesh = new THREE.Mesh(frontGeometry, material);
                frontMesh.position.z = rect.depth / 2 - rect.wallWidth / 2;
                frontMesh.position.y = rect.begin.y + rect.frontHeight / 2;
                frontMesh.castShadow = shadow;
                frontMesh.receiveShadow = shadow;

                object3D.add(frontMesh);
				}

				const bottomGeometry = new THREE.BoxGeometry(rect.width , rect.wallWidth, rect.depth, 1, 1, 1);
                const bottomMesh = new THREE.Mesh(bottomGeometry, material);
                bottomMesh.position.y = rect.begin.y + rect.wallWidth / 2;
                bottomMesh.castShadow = shadow;
                bottomMesh.receiveShadow = shadow;

                object3D.add(bottomMesh);
                object3D.position.x = rect.begin.x + rect.width / 2;
                if (rect.frontAngle == 0) {
                    object3D.position.z = rect.begin.z + (rect.getBoundingZ().max - rect.getBoundingZ().min) / 2;
                    return object3D;
                }
                object3D.position.y = -rect.rotationPoint.y;
                object3D.position.z = rect.begin.z + (rect.getBoundingZ().max - rect.getBoundingZ().min) / 2 - rect.rotationPoint.x;

                const obj1 = new THREE.Object3D();
                obj1.add(object3D);
                obj1.rotation.x = rect.frontAngle * Math.PI / 180;
                obj1.position.y += rect.rotationPoint.y;
                obj1.position.z += rect.rotationPoint.x;

                return obj1;
            }
			case 'Cylinder': {
				const cylinder: Cylinder = shape as Cylinder;
				const object3D = new THREE.Object3D();
				const geometry = new THREE.CylinderGeometry(cylinder.diameter / 2, cylinder.diameter / 2, cylinder.width, 32);
				const mesh = new THREE.Mesh(geometry, material);

				mesh.castShadow = shadow;
				mesh.receiveShadow = shadow;

				object3D.add(mesh);

				mesh.position.x = cylinder.begin.x + cylinder.width / 2;
				mesh.position.y = cylinder.begin.y;
				mesh.position.z = cylinder.begin.z;
				mesh.rotation.z = -90 * Math.PI / 180;

				const sideGeometry = new THREE.BoxGeometry(cylinder.diameter, cylinder.diameter, cylinder.begin.z - element.depend.getBoundingZ().max);

				const leftSideMesh = new THREE.Mesh(sideGeometry, material);
				object3D.add(leftSideMesh);
				leftSideMesh.position.x = cylinder.getBoundingX().min + cylinder.diameter / 2;
				leftSideMesh.position.y = cylinder.getBoundingY().min + cylinder.diameter / 2;
				leftSideMesh.position.z = element.depend.getBoundingZ().max + (cylinder.begin.z - element.depend.getBoundingZ().max) / 2;
				leftSideMesh.castShadow = shadow;
				leftSideMesh.receiveShadow = shadow;

				const rightSideMesh = new THREE.Mesh(sideGeometry, material);
				object3D.add(rightSideMesh);
				rightSideMesh.position.x = cylinder.getBoundingX().max - cylinder.diameter / 2;
				rightSideMesh.position.y = cylinder.getBoundingY().min + cylinder.diameter / 2;
				rightSideMesh.position.z = element.depend.getBoundingZ().max + (cylinder.begin.z - element.depend.getBoundingZ().max) / 2;
				rightSideMesh.castShadow = shadow;
				rightSideMesh.receiveShadow = shadow;

				return object3D;
			}
        }
    }

    getBoxShape(shape, material, shadow: boolean = true, isMesh: boolean = false) {
        const rect = shape as Rectangle,
            obj = new THREE.Object3D(),
            bottomGeometry = new THREE.BoxGeometry(rect.width, rect.wallWidth, rect.depth, 1, 1, 1);

		if (isMesh) {
			this.addBoxMeshWalls(obj, rect, material, shadow);
		} else {
			const sideGeometry = new THREE.BoxGeometry(rect.wallWidth, rect.height, rect.depth, 1, 1, 1);
			const frontGeometry = new THREE.BoxGeometry(rect.width, rect.height, rect.wallWidth, 1, 1, 1);

			const leftMesh = new THREE.Mesh(sideGeometry, material);
			leftMesh.position.x = rect.wallWidth / 2 - rect.width / 2;
			leftMesh.castShadow = shadow;
			leftMesh.receiveShadow = shadow;
			obj.add(leftMesh);

			const rightMesh = new THREE.Mesh(sideGeometry, material);
			rightMesh.position.x = rect.width / 2 - rect.wallWidth / 2;
			rightMesh.castShadow = shadow;
			rightMesh.receiveShadow = shadow;
			obj.add(rightMesh);

			const backMesh = new THREE.Mesh(frontGeometry, material);
			backMesh.position.z = rect.wallWidth / 2 - rect.depth / 2;
			backMesh.castShadow = shadow;
			backMesh.receiveShadow = shadow;
			obj.add(backMesh);

			const frontMesh = new THREE.Mesh(frontGeometry, material);
			frontMesh.position.z = rect.depth / 2 - rect.wallWidth / 2;
			frontMesh.castShadow = shadow;
			frontMesh.receiveShadow = shadow;
			obj.add(frontMesh);
		}

        const bottomMesh = new THREE.Mesh(bottomGeometry, material);
        bottomMesh.position.y = rect.wallWidth / 2 - rect.height / 2;
        bottomMesh.castShadow = shadow;
        bottomMesh.receiveShadow = shadow;
        obj.add(bottomMesh);

        obj.position.x = rect.begin.x + rect.width / 2;
        obj.position.y = rect.begin.y + rect.height / 2 - rect.rotationPoint.y;
        obj.position.z = rect.begin.z + rect.depth / 2 - rect.rotationPoint.x;
        const obj1 = new THREE.Object3D();
        obj1.add(obj);
        obj1.rotation.x = rect.frontAngle * Math.PI / 180;
        obj1.position.y += rect.rotationPoint.y;
        obj1.position.z += rect.rotationPoint.x;

        return obj1;
    }

	private addBoxMeshWalls(obj: THREE.Object3D, rect: Rectangle, material: THREE.Material, shadow: boolean) {
		const bar = Math.min(MESH_BAR_THICKNESS, rect.wallWidth, rect.height / 2, rect.depth / 2, rect.width / 2);

		if (bar <= 0) {
			return;
		}

		const addBox = (width: number, height: number, depth: number, x: number, y: number, z: number) => {
			const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);

			mesh.castShadow = mesh.receiveShadow = shadow;
			mesh.position.set(x, y, z);
			obj.add(mesh);
		};
		const xs = [rect.wallWidth / 2 - rect.width / 2, rect.width / 2 - rect.wallWidth / 2];
		const zs = [rect.wallWidth / 2 - rect.depth / 2, rect.depth / 2 - rect.wallWidth / 2];
		const cornerX = [-rect.width / 2 + rect.wallWidth / 2, rect.width / 2 - rect.wallWidth / 2];
		const cornerZ = [-rect.depth / 2 + rect.wallWidth / 2, rect.depth / 2 - rect.wallWidth / 2];

		cornerX.forEach((x) => {
			cornerZ.forEach((z) => {
				addBox(bar, rect.height, bar, x, 0, z);
			});
		});

		xs.forEach((x) => {
			getBoxInnerMeshVerticalOffsets(rect.depth, rect.wallWidth).forEach((offset) => {
				addBox(bar, rect.height, bar, x, 0, offset - rect.depth / 2);
			});
			getBoxInnerMeshHorizontalOffsets(rect.height).forEach((offset) => {
				addBox(bar, bar, rect.depth, x, offset - rect.height / 2, 0);
			});
		});

		zs.forEach((z) => {
			getBoxInnerMeshVerticalOffsets(rect.width, rect.wallWidth).forEach((offset) => {
				addBox(bar, rect.height, bar, offset - rect.width / 2, 0, z);
			});
			getBoxInnerMeshHorizontalOffsets(rect.height).forEach((offset) => {
				addBox(rect.width, bar, bar, 0, offset - rect.height / 2, z);
			});
		});
	}

	private addSlicedBoxMeshWalls(
		object3D: THREE.Object3D,
		rect: RectangleSliced,
		material: THREE.Material,
		shadow: boolean
	) {
		const bar = Math.min(MESH_BAR_THICKNESS, rect.wallWidth, Math.max(rect.frontHeight, rect.backHeight) / 2, rect.depth / 2, rect.width / 2);

		if (bar <= 0) {
			return;
		}

		const addBox = (
			width: number,
			height: number,
			depth: number,
			x: number,
			y: number,
			z: number,
			rotX: number = 0
		) => {
			const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);

			mesh.castShadow = mesh.receiveShadow = shadow;
			mesh.position.set(x, y, z);
			mesh.rotation.x = rotX;
			object3D.add(mesh);
		};
		const heightAtZ = (z: number) => {
			const t = rect.depth > 0 ? (z + rect.depth / 2) / rect.depth : 0;

			return rect.backHeight + t * (rect.frontHeight - rect.backHeight);
		};
		const slopeRotX = -Math.atan2(rect.frontHeight - rect.backHeight, rect.depth);
		const xs = [-rect.width / 2, rect.width / 2];
		const backZ = rect.wallWidth / 2 - rect.depth / 2;
		const frontZ = rect.depth / 2 - rect.wallWidth / 2;
		const cornerZ = [-rect.depth / 2 + rect.wallWidth / 2, rect.depth / 2 - rect.wallWidth / 2];
		const horizontalOffsetsBack = getBoxInnerMeshHorizontalOffsets(rect.backHeight);
		const horizontalOffsetsFront = getBoxInnerMeshHorizontalOffsets(rect.frontHeight);

		xs.forEach((x) => {
			cornerZ.forEach((z) => {
				const wallHeight = Math.max(heightAtZ(z), bar);

				addBox(bar, wallHeight, bar, x, rect.begin.y + wallHeight / 2, z);
			});
			getBoxInnerMeshVerticalOffsets(rect.depth, rect.wallWidth).forEach((offset) => {
				const z = offset - rect.depth / 2;
				const wallHeight = Math.max(heightAtZ(z), bar);

				addBox(bar, wallHeight, bar, x, rect.begin.y + wallHeight / 2, z);
			});
			getSlicedSlopeBars(rect.backHeight, rect.frontHeight, rect.depth, bar).forEach((span) => {
				const midZ = (span.min + span.max) / 2;
				const dz = span.max - span.min;
				const midY = span.yBack + ((rect.frontHeight - rect.backHeight) * midZ) / rect.depth;

				addBox(
					bar,
					bar,
					Math.hypot(dz, (rect.frontHeight - rect.backHeight) * (dz / rect.depth)),
					x,
					rect.begin.y + midY,
					midZ - rect.depth / 2,
					slopeRotX
				);
			});
		});

		getBoxInnerMeshVerticalOffsets(rect.width, rect.wallWidth).forEach((offset) => {
			const x = offset - rect.width / 2;

			addBox(bar, rect.backHeight, bar, x, rect.begin.y + rect.backHeight / 2, backZ);
			addBox(bar, rect.frontHeight, bar, x, rect.begin.y + rect.frontHeight / 2, frontZ);
		});
		horizontalOffsetsBack.forEach((offset) => {
			addBox(rect.width, bar, bar, 0, rect.begin.y + offset, backZ);
		});
		horizontalOffsetsFront.forEach((offset) => {
			addBox(rect.width, bar, bar, 0, rect.begin.y + offset, frontZ);
		});
	}

	async getProductObject(pg: ProductGroup, color: any): Promise<THREE.Object3D> {
		let material: THREE.MeshLambertMaterial;
		let additionalMaterial: THREE.MeshLambertMaterial;
		let productColor: string;
		let additionalColor: boolean = false;

		if (this.highlightProducts && this.highlightProducts.hasOwnProperty(pg.product.id)) {
			const highlight = this.highlightProductsService.getCorrectHighlight(
				this.highlightValue,
				this.highlightProducts,
				this.stand[0].products,
				pg
			);
			const emptyColor: Array<HighlightOptionsValue> = [
				HighlightOptionsValue.Doubles,
				HighlightOptionsValue.ProductHoles,
				HighlightOptionsValue.ProductHolesPrediction
			];

			if (emptyColor.includes(this.highlightValue) && !highlight.color) {
				highlight.color = konvaSettings.fillGreyColor;
			}

			productColor = highlight.color || color.mainColor;
			additionalColor = !!highlight.color && highlight.color != konvaSettings.fillGreyColor;
		} else if (this.rotatedProducts && Object.keys(this.rotatedProducts).includes(String(pg.product.id))) {
			productColor = 'green';
			additionalColor = true;
		} else {
			productColor = color.mainColor;
		}

		material = new THREE.MeshLambertMaterial({ color: pg.error ? konvaSettings.errorColor : productColor });

		if (this.photos && !pg.error && pg.image?.img?.src) {
			const useTransparentBackground: boolean = this.sessionService.personalSettings.transparentProductImagesBackground;
			const productImageSrc: string = ProductGroup.imageNotFound(pg.image.img.src)
				? EMPTY_PRODUCT_IMAGE_PATH
				: pg.image.img.src;
			const cacheSrc = `${productImageSrc}${pg.image.rotation || ''}_${useTransparentBackground ? 'transparent' : 'white'}`;
			const cachedMaterial = this.cache.get(cacheSrc);

			if (pg.availability && cachedMaterial) {
				cachedMaterial.transparent = true;
				cachedMaterial.alphaTest = 0.01;
				cachedMaterial.color = new THREE.Color(0xffffff);
				cachedMaterial.needsUpdate = true;
				material = cachedMaterial;
			}

			if (!pg.availability || !cachedMaterial) {
				this.createTextureWithBackground(productImageSrc, useTransparentBackground, (frontTexture) => {
						frontTexture.minFilter = THREE.LinearFilter;

						if (pg.image.rotation) {
							frontTexture.center = new THREE.Vector2(0.5, 0.5);
							frontTexture.rotation = -pg.image.rotation * Math.PI / 180;
						}

						frontTexture.colorSpace = THREE.SRGBColorSpace;
						material.color = new THREE.Color(0xffffff);
						material.map = frontTexture;
						material.transparent = useTransparentBackground;
						material.alphaTest = useTransparentBackground ? 0.01 : 0;
						material.needsUpdate = true;

						if (pg.availability) this.cache.set(cacheSrc, material);
					}
				);
			}

			if (additionalColor) {
				additionalMaterial = new THREE.MeshLambertMaterial({
					color: productColor,
					opacity: 0.5,
					transparent: true
				});
			}
		}

		const displayProductImageFrame: boolean = this.sessionService.personalSettings.displayProductImageFrame;
		const displayInactiveProductImageFrame: boolean = this.sessionService.personalSettings.displayInactiveProductImageFrame;
		const hasProductImageLayer: boolean = this.photos
			&& !pg.error
			&& !ProductGroup.imageNotFound(pg.image.img.src);
		const isProductImageFrameEnabled: boolean = pg.inactive
			? (displayInactiveProductImageFrame || displayProductImageFrame)
			: displayProductImageFrame;
		const useInactiveBlackFrame: boolean = pg.inactive && displayInactiveProductImageFrame;
		const productStrokeColor: string = useInactiveBlackFrame
			? konvaSettings.inactiveProductColor
			: konvaSettings.fillDarkColor;

		let borderColor: string = null;
		if (hasProductImageLayer) {
			if (isProductImageFrameEnabled) {
				borderColor = productStrokeColor;
			}
		} else {
			borderColor = productStrokeColor;
		}

		if (!pg.availability) {
			material.transparent = true;
			material.opacity = 0.25;
		}

		let height: number = pg.ItemHeight;
		let depth: number = pg.ItemDepth;

		if (pg.element.parent.is(EquipmentElements.Box) && pg.isLooseForm) {
			height = pg.element.shapes[0].height;
			depth = pg.element.shapes[0].depth - pg.element.shapes[0].wallWidth * 2;
		}

		if (pg.element.parent.is(EquipmentElements.SlicedBox) && pg.isLooseForm) {
			height = Math.min((pg.element.shapes[0] as RectangleSliced).frontHeight, (pg.element.shapes[0] as RectangleSliced).backHeight);
			depth = pg.element.shapes[0].depth - pg.element.shapes[0].wallWidth * 2;
		}

		const productObj: THREE.Object3D = new THREE.Object3D();
		const geometry = new THREE.BoxGeometry(pg.ItemWidth, height, depth, 1, 1, 1);
		const mesh = new THREE.Mesh(geometry, material);
		mesh.castShadow = true;
		mesh.receiveShadow = true;
		productObj.add(mesh);

		let additionalMesh: THREE.Mesh;
		if (additionalMaterial) {
			additionalMesh = new THREE.Mesh(geometry, additionalMaterial);
			additionalMesh.castShadow = true;
			additionalMesh.receiveShadow = true;
			productObj.add(additionalMesh);
		}

		if (borderColor) {
			const borderOpacity: number = !pg.availability ? 0.25 : 1;
			const borderTransparent: boolean = !pg.availability;
			const minDimension: number = Math.min(pg.ItemWidth, height, depth);

			if (pg.product.is_master_position) {
				const dashScale: number = minDimension / 35;
				const line = new THREE.LineSegments(
					new THREE.EdgesGeometry(geometry),
					new THREE.LineDashedMaterial({
						color: borderColor,
						transparent: borderTransparent,
						opacity: borderOpacity,
						dashSize: MASTER_POSITION_DASH[0] * dashScale,
						gapSize: MASTER_POSITION_DASH[1] * dashScale
					})
				);

				line.computeLineDistances();
				line.castShadow = false;
				line.receiveShadow = false;
				productObj.add(line);
			} else {
				const line = new THREE.LineSegments(
					new THREE.EdgesGeometry(geometry),
					new THREE.LineBasicMaterial({
						color: borderColor,
						transparent: borderTransparent,
						opacity: borderOpacity
					})
				);

				line.castShadow = false;
				line.receiveShadow = false;
				productObj.add(line);
			}
		}

		return productObj;
	}

	getHangerObject(pg: ProductGroup): THREE.Object3D {
		const material = new THREE.MeshBasicMaterial({ color: 0xff5a00 });

		const thickness: number = 0.002;
		const bottomLength: number = pg.product.width + 0.02;
		const sideLength: number = Math.sqrt(Math.pow(bottomLength / 2, 2) + Math.pow(0.07, 2));
		const angle: number = Math.atan2(0.07, bottomLength / 2);

		const bottomGeometry = new THREE.CylinderGeometry(thickness, thickness, bottomLength, 32);
		const bottomMesh = new THREE.Mesh(bottomGeometry, material);

		const leftGeometry = new THREE.CylinderGeometry(thickness, thickness, sideLength, 32);
		const leftMesh = new THREE.Mesh(leftGeometry, material);
		leftMesh.rotation.x = angle;
		leftMesh.position.y = -bottomLength / 4;
		leftMesh.position.z = 0.035;
		const rightGeometry = new THREE.CylinderGeometry(thickness, thickness, sideLength, 32);
		const rightMesh = new THREE.Mesh(rightGeometry, material);
		rightMesh.rotation.x = -angle;
		rightMesh.position.y = bottomLength / 4;
		rightMesh.position.z = 0.035;
		const topGeometry = new THREE.CylinderGeometry(thickness, thickness, 0.02, 32);
		const topMesh = new THREE.Mesh(topGeometry, material);
		topMesh.rotation.x = 90 * Math.PI / 180;
		topMesh.position.z = 0.08;
		const torusGeometry = new THREE.TorusGeometry(pg.element.Height / 2, thickness, 16, 100, 4);
		const torusMesh = new THREE.Mesh(torusGeometry, material);
		torusMesh.rotation.x = 2;
		torusMesh.rotation.y = 90 * Math.PI / 180;
		torusMesh.position.z = 0.09 + pg.element.Height / 2;

		const hanger = new THREE.Object3D();
		hanger.add(bottomMesh);
		hanger.add(leftMesh);
		hanger.add(rightMesh);
		hanger.add(topMesh);
		hanger.add(torusMesh);
		hanger.rotation.x = -90 * Math.PI / 180;

		if (pg.element.parent.is(EquipmentElements.HangBar)) {
			//hanger.position.x = pg.product.depth / 2;
			hanger.position.y = pg.product.height / 2 - 0.01;
			//hanger.position.z = -pg.product.width / 2;
		}

		if (pg.element.parent.is(EquipmentElements.Hook)) {
			hanger.rotation.z = 90 * Math.PI / 180;
			//hanger.position.x = pg.product.width / 2;
			hanger.position.y = pg.product.height / 2 - 0.01;
			//hanger.position.z = -pg.product.depth / 2;
		}
		return hanger;
	}

    async getProductObjects(pg: ProductGroup, color: any) {

        const onAngleShelf: boolean = pg.element.depend?.parent.type == 'AngleShelf',
            frontAngle = onAngleShelf ? (pg.element.depend.shapes[0] as any).frontAngle : (pg.element.shapes[0] as any).frontAngle;

        const obj = new THREE.Object3D();

        const facesQty: number = pg.isLooseForm ? 1 : Math.ceil(pg.qty / pg.placementDepth);
		const product: THREE.Object3D = await this.getProductObject(pg, color);
		let hanger: THREE.Object3D;

		if (pg.element.showsHangers()) {
			hanger = this.getHangerObject(pg);
		}

		if (pg.element.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox) && pg.isLooseForm) {
			const faceObj: THREE.Object3D = new THREE.Object3D();
			const wallWidth: number = pg.element.shapes[0].wallWidth * 2;
			let height: number = pg.element.shapes[0].height;

			if (pg.element.parent.is(EquipmentElements.SlicedBox)) {
				height = Math.min(pg.element.shapes[0].frontHeight, pg.element.shapes[0].backHeight);
			}

			faceObj.position.x = (pg.element.shapes[0].width - wallWidth) / 2;
			faceObj.position.y = height / 2;
			faceObj.position.z = (pg.element.shapes[0].depth - wallWidth) / 2;
			faceObj.add(product.clone());

			obj.add(faceObj);
		} else {
			for (let i = 0; i < facesQty; i++) {
				let x = i * pg.ItemWidth;

				for (let j = 0; j < pg.getPlacementDepth; j++) {
					let z = -j * pg.ItemDepth;

					for (let k = 0; k < pg.getPlacementHeight; k++) {
						let y = k * pg.ItemHeight;

						const faceObj: THREE.Object3D = new THREE.Object3D();

						faceObj.position.x = x + pg.ItemWidth / 2;
						faceObj.position.y = y + pg.ItemHeight / 2;
						faceObj.position.z = z - pg.ItemDepth / 2;
						faceObj.add(product.clone());
						if (hanger) faceObj.add(hanger.clone());

						obj.add(faceObj);
					}
				}
			}
		}

        if (frontAngle) {
			const rect: Rectangle = pg.element.shapes[0] as Rectangle;

			obj.position.x = pg.begin.x ;
			obj.position.y = pg.begin.y - rect.rotationPoint.y;
			obj.position.z = pg.begin.z - rect.rotationPoint.x;

            obj.rotation.y = -pg.angle * Math.PI / 180;

			const obj1 = new THREE.Object3D();
			obj1.add(obj);
			obj1.rotation.x = frontAngle * Math.PI / 180;
			obj1.position.y += rect.rotationPoint.y ;
			obj1.position.z += rect.rotationPoint.x;

			return [obj1];
        }

		obj.position.x = pg.begin.x;
		obj.position.y = pg.begin.y;
		obj.position.z = pg.begin.z;

        obj.rotation.y = -pg.angle * Math.PI / 180;

		return [obj];
    }

    drawCube(block) {
        const geometry = new THREE.BoxGeometry(block.width, block.height, block.depth, 1, 1, 1);
		let material: THREE.MeshBasicMaterial ;

		if (block.image) {
			material = this.cache.get(block.image);
			if (material) {
				material.transparent = true;
				material.alphaTest = 0.01;
				material.color = new THREE.Color(0xffffff);
				material.needsUpdate = true;
			}

			if (!material) {
				material = new THREE.MeshBasicMaterial({
					color: 0xffffff,
					transparent: true,
					alphaTest: 0.01
				});
				this.createTextureWithBackground(block.image, true, (frontTexture) => {
					frontTexture.colorSpace = THREE.SRGBColorSpace;
					material.map = frontTexture;
					material.transparent = true;
					material.alphaTest = 0.01;
					material.needsUpdate = true;
				});
				this.cache.set(block.image, material);
			}
		} else {
			material = new THREE.MeshBasicMaterial({
				color: block.color.slice(0, 7),
				transparent: true,
				opacity: block.colorAlpha
			});
		}
		const cube = new THREE.Mesh(geometry, material);

        cube.position.x = block.begin[0].x + block.width / 2;
        cube.position.y = block.stand.Height - block.begin[0].y - block.height / 2;
        cube.position.z = (block.begin[0].hasOwnProperty("z") ? block.begin[0].z : 0) + block.depth / 2;

        return [cube];
    }

	drawPOSImages(materials: Array<POSImageMaterial>) {
		return new Promise<Array<THREE.Group>>(async (resolve) => {
			const groups: Array<THREE.Group> = [];

			for (let  material of materials) {
				if (!material.image) {
					const group = await this.createFallbackTextMesh(material);
					groups.push(group);
					continue;
				}

				const image = new Image();
				image.crossOrigin = 'anonymous';
				image.src = material.image;
				const group = new THREE.Group();

				image.onload = () => {
					const texture = new THREE.Texture(image);
					texture.colorSpace = THREE.SRGBColorSpace;
					texture.needsUpdate = true;
					texture.generateMipmaps = false;
					texture.minFilter = THREE.LinearFilter;
					texture.wrapS = THREE.ClampToEdgeWrapping;
					texture.wrapT = THREE.ClampToEdgeWrapping;

					const geometry = new THREE.PlaneGeometry(material.width, material.height);
					const materialImage = new THREE.MeshBasicMaterial({
						map: texture,
						transparent: true,
						opacity: material.opacity,
						side: THREE.DoubleSide
					});
					const imageMesh = new THREE.Mesh(geometry, materialImage);

					geometry.translate(material.width / 2, -material.height / 2, 0);

					group.add(imageMesh);

					group.position.set(
						material.x,
						this.stand[0].Height - material.y,
						this.stand[0].Depth + 0.01
					);

					group.rotation.z = -(material.angle * Math.PI) / 180;
				}

				groups.push(group);
			}

			resolve(groups);
		});
	}

	drawPOSTexts(materials: Array<POSTextMaterial>) {
		return new Promise<Array<THREE.Group>>(async (resolve) => {
			const fontLoader = new FontLoader();
			const sharedCanvas: HTMLCanvasElement = document.createElement('canvas');
			const sharedContext: CanvasRenderingContext2D | null = sharedCanvas.getContext('2d');

			const wrapParagraph = (text: string, wrapWidthPx: number, fontSizePx: number): string[] => {
				sharedContext.font = `${fontSizePx}px Roboto Condensed, sans-serif`;

				const words: Array<string> = text.split(/[ \t]+/).filter(Boolean);
				const lines: string[] = [];
				let line = '';

				const pushLine = (value: string): void => {
					const cleaned = value.replace(/\r?\n/g, ' ').trim();
					if (cleaned.length === 0) return;
					lines.push(cleaned);
				};

				for (const word of words) {
					const testLine = line ? `${line} ${word}` : word;

					if (sharedContext.measureText(testLine).width <= wrapWidthPx) {
						line = testLine;
					} else {
						if (line !== '') {
							pushLine(line);
							line = '';
						}

						let subLine = '';
						for (let i = 0; i < word.length; i++) {
							const char = word[i];
							const testSubLine = subLine + char;

							if (sharedContext.measureText(testSubLine).width > wrapWidthPx) {
								if (subLine.length > 0) {
									pushLine(subLine);
								}
								subLine = char;
							} else {
								subLine = testSubLine;
							}
						}

						line = subLine;
					}
				}
				if (line !== '') {
					pushLine(line);
				}

				return lines;
			};

			const wrapText = (material: POSTextMaterial, wrapWidthPx: number, fontSizePx: number): string[] => {
				if (!sharedContext) return [material.name];

				const lines: string[] = [];
				const paragraphs: Array<string> = material.name.split(/\r?\n/);

				for (const paragraph of paragraphs) {
					const trimmed = paragraph.trim();
					if (trimmed.length === 0) continue;
					lines.push(...wrapParagraph(trimmed, wrapWidthPx, fontSizePx));
				}

				if (lines.length === 0 && material.name.trim().length > 0) {
					lines.push(material.name.replace(/\r?\n/g, ' ').trim());
				}

				return lines;
			};

			fontLoader.load(ROBOTO_CONDENSED_FONT_URL, (font) => {
				const groups: Array<THREE.Group> = [];
				for (const material of materials) {
					const textSize: number = material.fontSize / POS_TEXT_3D_SIZE_DIVISOR;
					const scale: number = POS_TEXT_WRAP_REF_SCALE;
					const fontSizePx: number = (material.fontSize * scale) / POS_TEXT_CANVAS_FONT_DIVISOR;
					const minTextWidthPx: number = (material.fontSize / POS_TEXT_CANVAS_FONT_DIVISOR) * scale;
					const isNarrow: boolean = material.width < material.fontSize / POS_TEXT_CANVAS_FONT_DIVISOR;
					const textBoxWidthPx: number = isNarrow ? minTextWidthPx : material.width * scale;
					const wrapWidthPx: number = Math.max(
						textBoxWidthPx - POS_TEXT_KONVA_PADDING_PX,
						fontSizePx
					);
					const alignWidth: number = material.width;
					const lines: string[] = wrapText(material, wrapWidthPx, fontSizePx);

					const group = new THREE.Group();
					const backgroundMaterial = this.createPOSTextBackgroundMaterial(material.backgroundColor);

					if (backgroundMaterial) {
						const backgroundGeometry = new THREE.PlaneGeometry(material.width, material.height);
						backgroundGeometry.translate(material.width / 2, -material.height / 2, 0);
						const backgroundMesh = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
						backgroundMesh.renderOrder = 0;
						group.add(backgroundMesh);
					}

					const lineMeshes: Array<{ mesh: THREE.Mesh; height: number }> = [];

					for (const line of lines) {
						const geometry = new TextGeometry(line, {
							font: font,
							size: textSize,
							depth: POS_TEXT_LABEL_DEPTH_M,
							curveSegments: 1,
							bevelEnabled: false
						});

						geometry.computeBoundingBox();
						const bbox = geometry.boundingBox;
						if (!bbox) continue;

						const lineHeight: number = Math.max(bbox.max.y - bbox.min.y, textSize);
						geometry.translate(-bbox.min.x, -bbox.max.y, 0);

						let horizontalOffset = 0;
						const lineWidth: number = bbox.max.x - bbox.min.x;
						switch (material.horizontalAlign) {
							case POSTextHorizontalAlign.Center:
								horizontalOffset = (alignWidth - lineWidth) / 2;
								break;
							case POSTextHorizontalAlign.Right:
								horizontalOffset = alignWidth - lineWidth;
								break;
						}

						const materialMesh = new THREE.MeshBasicMaterial({ color: material.color });
						const textMesh = new THREE.Mesh(geometry, materialMesh);
						textMesh.position.set(horizontalOffset, 0, POS_TEXT_LABEL_DEPTH_M);
						textMesh.renderOrder = 1;
						lineMeshes.push({ mesh: textMesh, height: lineHeight });
					}

					const lineGap: number = textSize * 0.15;
					const totalTextHeight: number = lineMeshes.reduce(
						(sum, item, index) => sum + item.height + (index > 0 ? lineGap : 0),
						0
					);

					let verticalOffset = 0;
					switch (material.verticalAlign) {
						case POSTextVerticalAlign.Middle:
							verticalOffset = -(material.height - totalTextHeight) / 2;
							break;
						case POSTextVerticalAlign.Bottom:
							verticalOffset = -(material.height - totalTextHeight);
							break;
					}

					let yCursor = verticalOffset;
					for (const item of lineMeshes) {
						item.mesh.position.y = yCursor;
						yCursor -= item.height + lineGap;
						item.mesh.matrixAutoUpdate = false;
						item.mesh.updateMatrix();
						group.add(item.mesh);
					}

					group.position.set(
						material.x,
						this.stand[0].Height - material.y,
						this.stand[0].Depth + POS_TEXT_LABEL_DEPTH_M
					);

					group.rotation.z = -(material.angle * Math.PI) / 180;

					groups.push(group);
				}

				resolve(groups);
			});
		});
	}

	private createPOSTextBackgroundMaterial(backgroundColor: string): THREE.MeshBasicMaterial | null {
		const hex = (backgroundColor ?? '').trim().replace(/^#/, '');
		if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) return null;

		const opacity = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
		if (opacity <= 0) return null;

		return new THREE.MeshBasicMaterial({
			color: new THREE.Color(parseInt(hex.slice(0, 6), 16)),
			transparent: opacity < 1,
			opacity,
			side: THREE.DoubleSide,
			depthWrite: opacity >= 1
		});
	}

	private createFallbackTextMesh(material: POSImageMaterial): Promise<THREE.Group> {
		return new Promise((resolve) => {
			const loader = new FontLoader();
			loader.load(ROBOTO_CONDENSED_FONT_URL, (font) => {
				const maxSize = 24 / 400;
				const minSize = 1 / 400;
				let currentSize = maxSize;

				let textGeometry: TextGeometry;
				let textWidth = Infinity;
				let bbox: THREE.Box3;

				while (currentSize >= minSize) {
					textGeometry = new TextGeometry(material.name, {
						font,
						size: currentSize,
						depth: POS_TEXT_LABEL_DEPTH_M,
						curveSegments: 1,
						bevelEnabled: false,
					});
					textGeometry.computeBoundingBox();
					bbox = textGeometry.boundingBox!;
					textWidth = bbox.max.x - bbox.min.x;

					if (textWidth <= material.width) break;
					currentSize -= 0.001;
				}

				const textHeight = bbox.max.y - bbox.min.y;
				const textMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
				const textMesh = new THREE.Mesh(textGeometry, textMaterial);

				const bgGeometry = new THREE.PlaneGeometry(material.width, material.height);
				const bgMaterial = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide });
				const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);

				bgGeometry.translate(material.width / 2, -material.height / 2, 0);

				const group = new THREE.Group();
				group.add(bgMesh);
				group.add(textMesh);

				textMesh.position.set(
					material.width / 2 - textWidth / 2 - bbox.min.x,
					-material.height / 2 - textHeight / 2 - bbox.min.y,
					0.001
				);

				group.position.set(
					material.x,
					this.stand[0].Height - material.y,
					this.stand[0].Depth + POS_TEXT_LABEL_DEPTH_M
				);
				group.rotation.z = -(material.angle * Math.PI) / 180;

				resolve(group);
			});
		});
	}

	cleanupAsyncObjects(): void {
		this._asyncObjects.forEach(obj => {
			obj.traverse((child) => {
				if (child instanceof THREE.Mesh) {
					if (child.geometry) {
						child.geometry.dispose();
					}

					if (Array.isArray(child.material)) {
						child.material.forEach((m) => m.dispose());
					} else if (child.material) {
						child.material.dispose();
					}
				}
			});

			if (obj.parent) {
				obj.parent.remove(obj);
			}
		});
		this._asyncObjects = [];
	}

	getRailObjects(shape: Figure, rails: Extension<RailsParams>, points: Array<number>, x: number): Array<THREE.Object3D> {
		const railsWidth: number = Math.min(rails.params.railHolesWidth, shape.width / 2 - 0.02);
		const railObjects: Array<THREE.Object3D> = [],
			railMaterial = new THREE.MeshPhongMaterial({ color: 0xb6b6b6, specular: 0xaaaaaa, shininess: 100 }),
			railGeometry = new THREE.BoxGeometry(railsWidth + 0.02, shape.height, 0.001, 1, 1, 1),
			railHolesMaterial = new THREE.MeshPhongMaterial({ colorWrite: false, specular: 0x0, shininess: 0 }),
			railHolesGeometry = new THREE.BoxGeometry(railsWidth, rails.params.railHolesHeight, 0.001, 1, 1, 1),
			railObj = new THREE.Object3D(),
			railMesh = new THREE.Mesh(railGeometry, railMaterial);

		railMesh.castShadow = true;
		railMesh.receiveShadow = true;
		railMesh.renderOrder = 1;

		railObj.add(railMesh);
		railObj.position.x = x ;
		railObj.position.y = shape.begin.y + shape.height / 2;
		railObj.position.z = shape.begin.z + shape.depth + 0.002;

		railObjects.push(railObj);

		points.forEach((point: number) => {
			const obj = new THREE.Object3D(),
				mesh = new THREE.Mesh(railHolesGeometry, railHolesMaterial);

			mesh.castShadow = true;
			mesh.receiveShadow = true;
			mesh.renderOrder = 0;

			obj.add(mesh);
			obj.position.x = x;
			obj.position.y = point + rails.params.railHolesHeight / 2;
			obj.position.z = shape.begin.z + shape.depth + 0.0025;

			railObjects.push(obj);
		});

		return railObjects;
	}

    getElementObjects(element: Element): THREE.Object3D[] {
        switch (element.parent.type) {
            case EquipmentElements.Box: {
				const material: THREE.MeshPhongMaterial = this.getElementMaterial(element);
				let shadow: boolean = true;

				if (element.hasExtension(ExtensionType.Availability) && !element.isExtensionActive(ExtensionType.Availability)) {
					shadow = false;
					material.transparent = true;
					material.opacity = INACTIVE_EQUIPMENT_OPACITY;
				}

                return [this.getBoxShape(element.shapes[0], material, shadow, element.isExtensionActive(ExtensionType.Mesh))];
            }
            case EquipmentElements.Showcase: {
                return element.shapes.map(shape => this.getShapeObject(shape, element, false));
            }
            case EquipmentElements.BackWall:
            case EquipmentElements.CentralPillar: {
				const rails: Extension<RailsParams> = element.getExtension<RailsParams>(ExtensionType.Rails),
					railObjects: Array<THREE.Object3D> = [];

				if (rails?.active) {
					const points: Array<number> = elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element),
						shape: Figure = element.shapes[0],
						sideWalls: Element = this.stand[0].allElements.find((el: Element) => {
							return el.parent.is(EquipmentElements.SideWalls) && el.depend == element.depend;
						}),
						leftSideWallWidth: number = sideWalls?.shapes[0].width || 0,
						rightSideWallWidth: number = sideWalls?.shapes[1].width || 0;
					const railsWidth: number = Math.min(rails.params.railHolesWidth, shape.width / 2 - 0.02);

					railObjects.push(
						...this.getRailObjects(
							shape,
							rails,
							points,
							shape.begin.x + leftSideWallWidth + (railsWidth + 0.02) / 2
						)
					);
					railObjects.push(
						...this.getRailObjects(
							shape,
							rails,
							points,
							shape.begin.x + shape.width - rightSideWallWidth - (railsWidth + 0.02) / 2
						)
					);
				}

                return [
					...element.shapes.map(shape => this.getShapeObject(shape, element)),
					...railObjects
				];
            }
            default:
                return element.shapes.map(shape => this.getShapeObject(shape, element));
        }
    }

    init(): void {
        try {
			this.inited = false;
            const width = this.parent ? this.parent.clientWidth : this.canvas.nativeElement.clientWidth,
                height = this.parent
					? (this.fullHeightPreview ? this.parent.clientHeight : (this.parent.clientHeight - 40))
					: this.canvas.nativeElement.clientHeight;

            this.scene = new THREE.Scene();
            this.scene.background = null;

            if (!this.sessionService.webglWorking) {
                console.warn('WebGL is not supported or working incorrectly. Changes will be saved with the old image.');
                this.webglError = true;
                this.error.emit(new Error('WebGL is not supported'));
                return;
            }

            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas.nativeElement,
                antialias: true,
                preserveDrawingBuffer: true
            });
            this.renderer.setPixelRatio(window.devicePixelRatio);
            this.renderer.setSize(width, height);
            this.renderer.setClearColor(0xffffff, 0);
            this.renderer.setClearAlpha(0);
            this.renderer.domElement.style.background = 'transparent';
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

            this.camera = new THREE.PerspectiveCamera(60, width / height, 0.01, 10000);
            this.camera.position.set(-2, 0, 3);

            this.controls = new OrbitControls(this.camera, this.renderer.domElement);
			this.controls.enableDamping = false;
			this.controls.rotateSpeed = 0.8;
			this.controls.zoomSpeed = 1.2;
			this.controls.panSpeed = 0.8;

	        this.animate();
            setTimeout(() => void this.updateScene());
	        this.inited = true;
        } catch (err) {
            this.error.emit(err);
        }
    }

	async updateScene(): Promise<boolean> {
		if (this.updating) {
			this.pendingUpdate = true;
			return false;
		}
		this.updating = true;

		do {
			this.pendingUpdate = false;
			await this.buildScene();
		} while (this.pendingUpdate);

		this.updating = false;
		return true;
	}

	private async buildScene(): Promise<void> {
		this.cleanupAsyncObjects();

		while (this.scene.children.length > 0) {
			this.scene.remove(this.scene.children[0]);
		}

        const light = new THREE.AmbientLight(0xffffff);
        this.scene.add(light);

		const hemi = new THREE.HemisphereLight(0xffffff, 0xe4e4e4, 1.0);
		this.scene.add(hemi);

		let offset = 0,
			height = 0,
			depth = 0;

		const group = new THREE.Object3D(),
			width = (this.stand as Equipment[]).map(st => st.Width + 0.05).reduce((a, b) => a + b, 0),
			block = new THREE.Object3D();

		for (const std of this.stand as Equipment[]) {
			const stand = new THREE.Object3D();
			std.allElements.forEach(el => {
				const elCopy = el.copy();
				if (elCopy.constructor.name == 'BoxElement' && elCopy.depend.parent.type == 'AngleShelf') {
					(elCopy.shapes[0] as any).frontAngle = (elCopy.depend.shapes[0] as any).frontAngle;
				}
				const objects = this.getElementObjects(elCopy);
				objects.forEach(obj => stand.add(obj));
			});

			if (this.products) {
				for (const [idx, product] of (std as EquipmentBase).products.entries()) {
					const materialIdx = idx % COLORS.length;
					const objects = await this.getProductObjects(product, COLORS[materialIdx]);

					objects.forEach(obj => {
						block.add(obj);
						this._asyncObjects.push(obj);
					});
				}
			}

			if (this.blocks) {
				this.blocks.forEach(b => {
					const objects = this.drawCube(b);
					objects.forEach(obj => {
						stand.add(obj);
						this._asyncObjects.push(obj);
					});
				});
			}

			const textMaterials: Array<POSTextMaterial> = this.POSMaterials.filter(
				(material: POSMaterial): material is POSTextMaterial => material instanceof POSTextMaterial
			);
			const imageMaterials: Array<POSImageMaterial> = this.POSMaterials.filter(
				(material: POSMaterial): material is POSImageMaterial => material instanceof POSImageMaterial
			);

            if (textMaterials.length) {
                const textMeshes = await this.drawPOSTexts(textMaterials);
                textMeshes.forEach(obj => {
                    stand.add(obj);
                    this._asyncObjects.push(obj);
                });
            }

            if (imageMaterials.length) {
                const imageMeshes = await this.drawPOSImages(imageMaterials);

                imageMeshes.forEach(obj => {
                    stand.add(obj);
                    this._asyncObjects.push(obj);
                });
            }
			
			stand.position.x = offset;
			group.add(stand);
			group.add(block);

			const directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
			directionalLight.position.set(offset + (std.Width / 2) - (width / 2) + 2, 2, 5);

			directionalLight.castShadow = true;
			// directionalLight.shadow = new THREE.LightShadow( new THREE.PerspectiveCamera( 30, 1,  scale  * 3 , scale * 10 ));
			directionalLight.target = stand;

			directionalLight.shadow.mapSize.width = 1024;
			directionalLight.shadow.mapSize.height = 1024;

			this.scene.add(directionalLight);

			offset += std.Width + 0.05;
			height = Math.max(height, std.Height);
			depth = Math.max(depth, std.Depth);
		}

		group.position.x = -offset / 2;
		group.position.y = -height / 2;
		group.position.z = -depth / 2;

		this.scene.add(group);
    }

    render() {
        if (this.renderer) this.renderer.render(this.scene, this.camera);
    }

    private static readonly EXCEL_2D_SCREENSHOT_BASE_HEIGHT = 1100;
    private static readonly EXCEL_SHEET_IMAGE_SCALE = 0.3;
    private static readonly EXCEL_INSERT_SIZE_COMPENSATION = 1 / EquipmentModelComponent.EXCEL_SHEET_IMAGE_SCALE;
    private static readonly SVG_SCREENSHOT_SCALE = 250;
    private static readonly SVG_SCREENSHOT_PADDING_X = 20;
    private static readonly SVG_SCREENSHOT_PADDING_Y = 40;
    private static readonly MAX_EXPORT_LONG_EDGE = 8192;
    private static readonly PDF_EXPORT_LONG_EDGE = 2560;
    private static readonly PDF_EXPORT_WHITE_THRESHOLD = 8;

    getExportImage(fromCameraView: boolean = false, cropToContent: boolean = true): string | null {
        if (!this.renderer || !this.sessionService.webglWorking) {
            return null;
        }

        const size = new THREE.Vector2();
        this.renderer.getSize(size);

        if (fromCameraView && size.x && size.y) {
            const previewSize = this.getPreviewExportSize();

            return this.exportImageFromCameraView(previewSize.width, previewSize.height, cropToContent);
        }

        if (!size.x || !size.y) {
            return this.getImage(1);
        }

        let targetWidth = 1920 * EquipmentModelComponent.EXCEL_INSERT_SIZE_COMPENSATION;
        let targetHeight = 1920 * EquipmentModelComponent.EXCEL_INSERT_SIZE_COMPENSATION;

        const standForExport = this.getStandForExport();
        if (standForExport) {
            const bounds = Utils.calculatePlanogramBounds(standForExport);
            targetWidth = (Math.ceil(bounds.width * EquipmentModelComponent.SVG_SCREENSHOT_SCALE)
                + EquipmentModelComponent.SVG_SCREENSHOT_PADDING_X)
                * EquipmentModelComponent.EXCEL_INSERT_SIZE_COMPENSATION;
            targetHeight = (Math.ceil(bounds.height * EquipmentModelComponent.SVG_SCREENSHOT_SCALE)
                + EquipmentModelComponent.SVG_SCREENSHOT_PADDING_Y)
                * EquipmentModelComponent.EXCEL_INSERT_SIZE_COMPENSATION;
        }

        let scale = Math.max(targetWidth / size.x, targetHeight / size.y, 1);
        const longEdge = Math.max(size.x * scale, size.y * scale);

        if (longEdge > EquipmentModelComponent.MAX_EXPORT_LONG_EDGE) {
            scale *= EquipmentModelComponent.MAX_EXPORT_LONG_EDGE / longEdge;
        }

        return this.getImage(scale);
    }

	private getCameraViewExportUpscale(width: number, height: number, cropToContent: boolean): number {
		const maxUpscale = EquipmentModelComponent.MAX_EXPORT_LONG_EDGE / Math.max(width, height);

		if (cropToContent) {
			const upscale = EquipmentModelComponent.PDF_EXPORT_LONG_EDGE / Math.max(width, height);

			return Math.min(Math.max(upscale, 1), maxUpscale);
		}

		// 2D: as_jpg scales SVG to base_height and caps raster at base_height * 2, then Excel uses ×0.3.
		const targetRenderHeight = EquipmentModelComponent.EXCEL_2D_SCREENSHOT_BASE_HEIGHT * 2;
		const aspect = width / height;
		const targetRenderWidth = targetRenderHeight * aspect;
		const upscale = Math.max(targetRenderWidth / width, targetRenderHeight / height, 1);

		return Math.min(upscale, maxUpscale);
	}

	private getPreviewExportSize(): { width: number; height: number } {
		const rendererSize = new THREE.Vector2();
		this.renderer.getSize(rendererSize);

		if (this.parent) {
			const parentWidth = this.parent.clientWidth;
			const parentHeight = this.parent.clientHeight;

			if (parentWidth > 0 && parentHeight > 0) {
				return { width: parentWidth, height: parentHeight };
			}
		}

		return { width: rendererSize.x, height: rendererSize.y };
	}

	private renderSceneToWhiteCanvas(outputWidth: number, outputHeight: number): HTMLCanvasElement | null {
		const outputCanvas = document.createElement('canvas');
		outputCanvas.width = outputWidth;
		outputCanvas.height = outputHeight;
		const outputContext = outputCanvas.getContext('2d');

		if (!outputContext) {
			return null;
		}

		const glCanvas = this.renderer.domElement;

		outputContext.fillStyle = '#ffffff';
		outputContext.fillRect(0, 0, outputWidth, outputHeight);
		outputContext.drawImage(
			glCanvas,
			0,
			0,
			glCanvas.width,
			glCanvas.height,
			0,
			0,
			outputWidth,
			outputHeight
		);

		return outputCanvas;
	}

	private setupExportViewport(width: number, height: number): void {
		this.renderer.setPixelRatio(1);
		this.renderer.setSize(width, height, false);
		this.camera.aspect = width / height;
		this.camera.updateProjectionMatrix();
		this.render();
	}

	private exportImageFromCameraView(width: number, height: number, cropToContent: boolean = true): string | null {
		this.render();

		const originalSize = new THREE.Vector2();
		this.renderer.getSize(originalSize);
		const originalPixelRatio = this.renderer.getPixelRatio();
		const upscale = this.getCameraViewExportUpscale(width, height, cropToContent);
		const renderWidth = Math.max(Math.floor(width * upscale), 1);
		const renderHeight = Math.max(Math.floor(height * upscale), 1);

		this.setupExportViewport(renderWidth, renderHeight);

		const outputCanvas = this.renderSceneToWhiteCanvas(renderWidth, renderHeight);

		this.restoreRendererViewport(originalSize, originalPixelRatio);

		if (!outputCanvas) {
			return this.renderer.domElement.toDataURL('image/png');
		}

		if (cropToContent) {
			return this.cropCanvasToContentBounds(outputCanvas).toDataURL('image/png');
		}

		return outputCanvas.toDataURL('image/png');
	}

	private isCanvasContentPixel(
		data: Uint8ClampedArray,
		offset: number,
		whiteThreshold: number
	): boolean {
		const alpha = data[offset + 3];

		if (alpha < 8) {
			return false;
		}

		if (alpha < 245) {
			return true;
		}

		const r = data[offset];
		const g = data[offset + 1];
		const b = data[offset + 2];

		return (255 - r) + (255 - g) + (255 - b) >= whiteThreshold;
	}

	private computeCanvasContentBounds(
		context: CanvasRenderingContext2D,
		width: number,
		height: number
	): { minX: number; minY: number; maxX: number; maxY: number } | null {
		const imageData = context.getImageData(0, 0, width, height);
		const data = imageData.data;
		const whiteThreshold = EquipmentModelComponent.PDF_EXPORT_WHITE_THRESHOLD;
		let minX = width;
		let minY = height;
		let maxX = -1;
		let maxY = -1;

		for (let y = 0; y < height; y++) {
			const rowOffset = y * width * 4;

			for (let x = 0; x < width; x++) {
				if (!this.isCanvasContentPixel(data, rowOffset + x * 4, whiteThreshold)) {
					continue;
				}

				if (x < minX) {
					minX = x;
				}

				if (x > maxX) {
					maxX = x;
				}

				if (y < minY) {
					minY = y;
				}

				if (y > maxY) {
					maxY = y;
				}
			}
		}

		if (maxX < minX || maxY < minY) {
			return null;
		}

		const bounds = { minX, minY, maxX, maxY };
		this.shrinkBoundsToSolidContent(data, width, height, bounds, whiteThreshold);

		return bounds;
	}

	private shrinkBoundsToSolidContent(
		data: Uint8ClampedArray,
		width: number,
		height: number,
		bounds: { minX: number; minY: number; maxX: number; maxY: number },
		whiteThreshold: number
	): void {
		const minContentPixels = 2;

		const countContentInRow = (y: number): number => {
			let count = 0;
			const rowOffset = y * width * 4;

			for (let x = bounds.minX; x <= bounds.maxX; x++) {
				if (this.isCanvasContentPixel(data, rowOffset + x * 4, whiteThreshold)) {
					count++;
				}
			}

			return count;
		};

		const countContentInColumn = (x: number): number => {
			let count = 0;

			for (let y = bounds.minY; y <= bounds.maxY; y++) {
				if (this.isCanvasContentPixel(data, y * width * 4 + x * 4, whiteThreshold)) {
					count++;
				}
			}

			return count;
		};

		while (bounds.minY < bounds.maxY && countContentInRow(bounds.minY) < minContentPixels) {
			bounds.minY++;
		}

		while (bounds.minY < bounds.maxY && countContentInRow(bounds.maxY) < minContentPixels) {
			bounds.maxY--;
		}

		while (bounds.minX < bounds.maxX && countContentInColumn(bounds.minX) < minContentPixels) {
			bounds.minX++;
		}

		while (bounds.minX < bounds.maxX && countContentInColumn(bounds.maxX) < minContentPixels) {
			bounds.maxX--;
		}
	}

	private cropCanvasToContentBounds(sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
		const outputWidth = sourceCanvas.width;
		const outputHeight = sourceCanvas.height;
		const sourceContext = sourceCanvas.getContext('2d');

		if (!sourceContext) {
			return sourceCanvas;
		}

		const bounds = this.computeCanvasContentBounds(sourceContext, outputWidth, outputHeight);

		if (!bounds) {
			return sourceCanvas;
		}

		const cropWidth = bounds.maxX - bounds.minX + 1;
		const cropHeight = bounds.maxY - bounds.minY + 1;

		if (cropWidth < 1 || cropHeight < 1) {
			return sourceCanvas;
		}

		if (cropWidth >= outputWidth && cropHeight >= outputHeight) {
			return sourceCanvas;
		}

		const croppedCanvas = document.createElement('canvas');
		croppedCanvas.width = cropWidth;
		croppedCanvas.height = cropHeight;
		const croppedContext = croppedCanvas.getContext('2d');

		if (!croppedContext) {
			return sourceCanvas;
		}

		croppedContext.fillStyle = '#ffffff';
		croppedContext.fillRect(0, 0, cropWidth, cropHeight);
		croppedContext.drawImage(
			sourceCanvas,
			bounds.minX,
			bounds.minY,
			cropWidth,
			cropHeight,
			0,
			0,
			cropWidth,
			cropHeight
		);

		return croppedCanvas;
	}

    private getStandForExport(): EquipmentBase | null {
        const value = Array.isArray(this.stand) ? this.stand[0] : this.stand;

        if (!value || !('products' in value)) {
            return null;
        }

        return value as EquipmentBase;
    }

    getImage(scale: number = 1) {
        try {
            if (!this.renderer || !this.sessionService.webglWorking) {
                console.warn('WebGL is not working, returning null image');
                return null;
            }
			if (!scale || scale <= 1) {
				return this.renderer.domElement.toDataURL('image/png');
			}

			const originalSize = new THREE.Vector2();
			this.renderer.getSize(originalSize);
			const originalPixelRatio = this.renderer.getPixelRatio();
			const width = Math.max(Math.floor(originalSize.x * scale), 1);
			const height = Math.max(Math.floor(originalSize.y * scale), 1);

			this.setupExportViewport(width, height);

			const outputCanvas = this.renderSceneToWhiteCanvas(width, height);
			const highResImage = outputCanvas?.toDataURL('image/png')
				?? this.renderer.domElement.toDataURL('image/png');

			this.restoreRendererViewport(originalSize, originalPixelRatio);

			return highResImage;
        } catch (error) {
            console.error('Error getting image:', error);
            return null;
        }
    }

	private restoreRendererViewport(size: THREE.Vector2, pixelRatio: number): void {
		this.renderer.setPixelRatio(pixelRatio);
		this.renderer.setSize(size.x, size.y, false);
		this.camera.aspect = size.x / size.y;
		this.camera.updateProjectionMatrix();
		this.render();
	}

    openSupport(): void {
		window.$crisp.push(['do', 'chat:open']);
    }

    animate() {
        if (!this.renderer) return;
        this.render();
        this.animationFrame = requestAnimationFrame(() => this.animate());
    }

    ngOnDestroy() {
		this.cleanupAsyncObjects();

		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = undefined;
		}

	    if (this.controls) {
		    this.controls.dispose();
		    this.controls = undefined;
	    }

	    if (this.scene) {
		    this.scene.traverse((child) => {
			    if (child instanceof THREE.Mesh) {
				    if (child.geometry) child.geometry.dispose();
				    if (Array.isArray(child.material)) {
					    child.material.forEach(m => m.dispose());
				    } else if (child.material) {
					    child.material.dispose();
				    }
			    }
		    });

		    while (this.scene.children.length > 0) {
			    this.scene.remove(this.scene.children[0]);
		    }

		    this.scene = undefined;
	    }

	    if (this.renderer) {
		    if (this.renderer.domElement?.parentNode) {
			    this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
		    }

		    this.renderer.dispose();
		    this.renderer = undefined;
	    }

	    if (this.camera) this.camera = undefined;
    }
}